import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Calendar, ListChecks, Layers, Users, Clock, LogOut, Loader2, Database } from "lucide-react";
import CalendarTab from "./CalendarTab";
import AppointmentsTab from "./AppointmentsTab";
import TypesTab from "./TypesTab";
import PharmacistsTab from "./PharmacistsTab";
import AvailabilityTab from "./AvailabilityTab";
import DataTab from "./DataTab";

const TABS = [
    { key: "calendar", label: "Calendrier", icon: Calendar },
    { key: "appointments", label: "Rendez-vous", icon: ListChecks },
    { key: "types", label: "Types de RDV", icon: Layers },
    { key: "pharmacists", label: "Pharmaciens", icon: Users },
    { key: "availability", label: "Disponibilités", icon: Clock },
    { key: "data", label: "Données", icon: Database, hidden: true },
];
// "Données" (copy to Swiss DB) is kept for a future migration; set hidden: false to show it.
const VISIBLE_TABS = TABS.filter((t) => !t.hidden);

export default function AdminDashboard() {
    const { user, loading, logout } = useAuth();
    const [tab, setTab] = useState("calendar");

    if (loading) {
        return <div className="min-h-screen bg-brand-cream flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
        </div>;
    }
    if (!user) return <Navigate to="/admin/login" replace />;

    return (
        <div className="min-h-screen bg-brand-cream" data-testid="admin-dashboard">
            <header className="bg-white border-b border-brand-line">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
                    <a href="/" className="inline-flex items-center gap-3">
                        <img src="/brand/logo-300.webp" alt="La Lutrive" className="h-12 w-auto" />
                        <span className="ll-eyebrow text-brand-primary">Backoffice</span>
                    </a>
                    <div className="flex items-center gap-4">
                        {user.picture && <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />}
                        <div className="text-right hidden sm:block">
                            <p className="font-sans text-sm text-brand-ink">{user.name || user.email}</p>
                            <p className="text-xs text-brand-ink/55">{user.email}</p>
                        </div>
                        <button onClick={logout} className="ll-btn ll-btn-ghost text-sm" data-testid="logout-btn">
                            <LogOut className="h-4 w-4" /> Déconnexion
                        </button>
                    </div>
                </div>

                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex gap-1 overflow-x-auto no-scrollbar">
                    {VISIBLE_TABS.map(t => {
                        const I = t.icon;
                        const active = tab === t.key;
                        return (
                            <button key={t.key} onClick={() => setTab(t.key)}
                                className={`px-4 py-3 inline-flex items-center gap-2 font-sans text-sm transition-colors border-b-2 -mb-px whitespace-nowrap
                                    ${active ? "border-brand-primary text-brand-primary" : "border-transparent text-brand-ink/55 hover:text-brand-ink"}`}
                                data-testid={`tab-${t.key}`}>
                                <I className="h-4 w-4" /> {t.label}
                            </button>
                        );
                    })}
                </div>
            </header>

            <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
                {tab === "calendar" && <CalendarTab />}
                {tab === "appointments" && <AppointmentsTab />}
                {tab === "types" && <TypesTab />}
                {tab === "pharmacists" && <PharmacistsTab />}
                {tab === "availability" && <AvailabilityTab />}
                {tab === "data" && !TABS.find((t) => t.key === "data").hidden && <DataTab />}
            </main>
        </div>
    );
}
